//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import axios from 'axios';
import moment from 'moment'; // Using moment.js for easier date calculations

export const formatInt = (num: number | null | undefined): string => {
  return typeof num === 'number' ? num.toLocaleString() : '';
};

/**
 * A round number said the short way: 10000 as "10k", 1500000 as "1.5M".
 *
 * For numbers somebody picks from rather than numbers about their data -- a menu of sample
 * sizes, where the magnitude is the whole of what is being chosen and the digits are noise.
 * One decimal at most, and only where the rest is zeroes: "1.5M" is a magnitude, "1.53M" is a
 * number with its digits taken away, and "1,234" says something "1.2k" does not.  Uppercase M
 * and lowercase k, as SI has them -- a lowercase m is a thousandth.
 */
export const formatShortInt = (num: number): string => {
  const size = Math.abs(num);
  if (size >= 1_000_000 && num % 100_000 === 0)
    return `${num / 1_000_000}M`;
  if (size >= 1_000 && num % 100 === 0) return `${num / 1_000}k`;
  return formatInt(num);
};

export const formatFloat = (
  num: number | null | undefined, digits = 2
): string => {
  return typeof num === 'number' ?
         num.toLocaleString(undefined,
                            { minimumFractionDigits: digits,
                              maximumFractionDigits: digits  }) :
         '';
};

// CPU percent, where 100% is one core.  Matches xgttop's format_percent: one
// decimal below 100%, none above, so a column of values stays the same width
// once a job goes multi-core.  Empty string rather than a dash, since the
// table's other cells use empty for "not reported".
export const formatCpuPercent = (
  cores: number | null | undefined
): string => {
  if (typeof cores !== 'number')
    return '';
  const percent = cores * 100;
  return percent >= 100 ? percent.toFixed(0) : percent.toFixed(1);
};

// CPU core-seconds as M:SS.hh, or H:MM:SS past an hour -- top's TIME+ column,
// and the same rendering xgttop uses so the two agree on sight.
export const formatCpuTime = (
  seconds: number | null | undefined
): string => {
  if (typeof seconds !== 'number')
    return '';
  // Round to the displayed precision *before* splitting into fields, so a
  // remainder that rounds up carries into the minute instead of rendering an
  // out-of-range 0:60.00.  Integer centiseconds rather than a rounded float,
  // so the split is exact.
  const centis = Math.round(seconds * 100);
  if (centis < 360000) {
    const minutes = Math.floor(centis / 6000);
    const rest = (centis - minutes * 6000) / 100;
    return `${minutes}:${rest.toFixed(2).padStart(5, '0')}`;
  }
  // Past an hour the seconds field drops its fraction, as top's TIME+ does.
  const total = Math.floor(centis / 100);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total - hours * 3600) / 60);
  const secs = total - hours * 3600 - minutes * 60;
  return `${hours}:${String(minutes).padStart(2, '0')}:` +
         `${String(secs).padStart(2, '0')}`;
};

export const formatDate = (
  date: string | number | Date | null | undefined
): string => {
  if (date == null || date === '')
    return '';

  if (date instanceof Date)
    return date.toLocaleDateString();

  return new Date(date).toLocaleDateString();
};

export const formatDateTime = (
  dateTime: string | number | Date | null | undefined
): string => {
  if (dateTime == null || dateTime === '')
    return '';

  if (dateTime instanceof Date)
    return dateTime.toLocaleString();

  return new Date(dateTime).toLocaleString();
};

export function formatCypher(queryStr: string) {
  // Add newline before WHERE, RETURN, and WITH.
  return queryStr.replace(/\s+(MATCH|OPTIONAL MATCH|WHERE|RETURN|LIMIT|SKIP|ORDER BY|UNWIND|CREATE|MERGE|SET|DELETE|DETACH DELETE|WITH|CALL|YIELD)/gi, '\n$1');
}

// Returns the difference between two timestamps in the format "0h 0m 0s",
// gaining a days component once the duration crosses 24 hours.
export const calculateDuration = (startStr: string, endStr: string) => {
  const startYear = new Date(startStr).getFullYear();
  const endYear = new Date(endStr).getFullYear();
  // Negated >= so an unparseable date (NaN year) also fails the check.
  if (!startStr || !endStr || !(startYear >= 1970) || !(endYear >= 1970)) {
    return 'N/A'; // Handles missing, unparseable, and pre-epoch dates
  }

  const start = moment(startStr);
  const end = moment(endStr);

  // A live "now" endpoint can sit slightly behind a server-supplied start
  // time (client/server clock skew); clamp to zero rather than failing.
  const duration = moment.duration(Math.max(0, end.diff(start)));
  const days = Math.floor(duration.asDays());
  const hours = duration.hours();
  const minutes = duration.minutes();
  const seconds =
      (duration.seconds() + duration.milliseconds() / 1000).toFixed(3);
  if (days > 0)
    return `${days}d ${hours}h ${minutes}m ${seconds}s`;
  else if (hours > 0)
    return `${hours}h ${minutes}m ${seconds}s`;
  else if (minutes > 0)
    return `${minutes}m ${seconds}s`;

  return `${seconds}s`;
};

// Coarse relative timestamp ("5m ago"); pair it with an absolute time in a
// tooltip where precision matters.
export function formatRelativeTime(iso: string): string {
  const t = new Date(iso).getTime();
  if (!isFinite(t)) return 'N/A';
  const diffS = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (diffS < 60) return 'just now';
  const m = Math.floor(diffS / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export const unitMultipliers: Record<string, number> = {
  bytes: 1,
  KiB: 1024,
  MiB: 1024 * 1024,
  GiB: 1024 * 1024 * 1024,
  TiB: 1024 * 1024 * 1024 * 1024,
};

const MIN_BLOCK_BYTES = 1024;                    // 1 KiB
const MAX_BLOCK_BYTES = 50 * 1024 * 1024 * 1024; // 50 GiB

export function clampBlockBytes(bytes: number) {
  if (bytes < MIN_BLOCK_BYTES) return MIN_BLOCK_BYTES;
  if (bytes > MAX_BLOCK_BYTES) return MAX_BLOCK_BYTES;
  return bytes;
}

export function getFileSizeString(fileSize: number) {
  const fileSizeInKiB = (fileSize / 1024).toFixed(2);
  const fileSizeInMiB = (fileSize / (1024 * 1024)).toFixed(2);
  const fileSizeInGiB = (fileSize / (1024 * 1024 * 1024)).toFixed(2);
  const fileSizeInTiB = (fileSize / (1024 * 1024 * 1024 * 1024)).toFixed(2);

  let sizeString;

  if (fileSize >= 1024 * 1024 * 1024 * 1024) {
    sizeString = `${fileSizeInTiB} TiB`;
  } else if (fileSize >= 1024 * 1024 * 1024) {
    sizeString = `${fileSizeInGiB} GiB`;
  } else if (fileSize >= 1024 * 1024) {
    sizeString = `${fileSizeInMiB} MiB`;
  } else {
    sizeString = `${fileSizeInKiB} KiB`;
  }

  return sizeString;
}

export function getFileSizeUnit(fileSize: number): [number, string] {
  const fileSizeInKiB = (fileSize / 1024);
  const fileSizeInMiB = (fileSize / (1024 * 1024));
  const fileSizeInGiB = (fileSize / (1024 * 1024 * 1024));

  if (fileSize >= 1024 * 1024 * 1024) {
    return [fileSizeInGiB, 'GiB'];
  } else if (fileSize >= 1024 * 1024) {
    return [fileSizeInMiB, 'MiB'];
  }

  return [fileSizeInKiB, 'KiB'];
}

// The readable text in a backend error body's `error` field, which arrives
// as a string, a list of strings, or an object carrying a `message`. Nothing
// readable yields undefined rather than "[object Object]".
const errorBodyText = (detail: unknown): string | undefined => {
  if (typeof detail === 'string')
    return detail === '' ? undefined : detail;
  if (Array.isArray(detail)) {
    const parts = detail.filter(
      (part): part is string => typeof part === 'string' && part !== '');
    return parts.length > 0 ? parts.join('; ') : undefined;
  }
  if (detail !== null && typeof detail === 'object' && 'message' in detail)
    return errorBodyText(detail.message);
  return undefined;
};

export function getErrorMessage(error: unknown): string {
  // Axios error: the backend's own explanation, when the body carries one;
  // otherwise the transport message.
  if (axios.isAxiosError(error)) {
    const detail = errorBodyText(error.response?.data?.error);
    if (detail !== undefined)
      return detail;
  }
  // Standard Error object.
  if (error instanceof Error)
    return error.message;
  // String thrown directly.
  if (typeof error === 'string')
    return error;
  return `${error}`;
}

export function isValidUrl(url: string) {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

// Uint8Array.fromBase64() is not yet in TypeScript's lib definitions.
declare global {
  interface Uint8ArrayConstructor {
    fromBase64?(
      encoded: string,
      options?: { alphabet: string }
    ): Uint8Array;
  }
}

export function decodeBase64UrlCompat(encoded: string) {
  // --- Modern browsers / Node ≥ 20 ---
  if (typeof Uint8Array.fromBase64 === 'function') {
    return new TextDecoder().decode(
      Uint8Array.fromBase64(encoded, { alphabet: 'base64url' })
    );
  }

  // --- Fallback for older browsers ---
  // 1. Convert from Base64URL → standard Base64
  let base64 = encoded.replace(/-/g, '+').replace(/_/g, '/');
  // 2. Add missing padding
  while (base64.length % 4) base64 += '=';

  // 3. Decode Base64 → binary string
  const binary = atob(base64);
  // 4. Convert binary string → bytes
  const bytes = Uint8Array.from(binary, c => c.charCodeAt(0));
  // 5. Decode UTF-8 bytes → string
  return new TextDecoder().decode(bytes);
}

// Converts the given object to a string representation if not already a string.
export function ensureString(object: unknown): string {
  if (typeof object === 'string') return object;
  if (object == null) return '';

  if (typeof object === 'object') {
    // Render objects/arrays as a readable multiline block.
    if (Array.isArray(object)) {
      // Let formatValue handle the entire array formatting, including bullets
      // and indentation.
      return formatValue(object, 0);
    }

    // For objects, format each value with an indent of 1 relative to its key.
    return Object.entries(object)
      .map(([k, v]) => `${titleCase(k)}\n${formatValue(v, 1)}`)
      .join('\n\n');
  }

  return String(object);
}

function titleCase(key: string) {
  return key
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatValue(v: unknown, indent: number): string {
  const currentPad = '  '.repeat(indent);

  if (v == null)
    return currentPad + String(v);

  let formattedContent;
  if (typeof v === 'string') {
    formattedContent = v.trim();
  } else if (Array.isArray(v)) {
    return v.map(item => {
      const rendered = formatValue(item, indent + 1);
      if (!rendered.trim())
        return currentPad + '-';

      const lines = rendered.split('\n');
      return [
        `${currentPad}- ${lines[0].trim()}`,
        ...lines.slice(1)
      ].join('\n');
    }).join('\n');
  } else if (typeof v === 'object') {
    // Pretty JSON for nested structures.
    // JSON.stringify(v, null, 2) produces a block with 2-space internal indent.
    // We then need to prefix *each line* of this block with `currentPad`.
    formattedContent = JSON.stringify(v, null, 2);
  } else {
    formattedContent = String(v);
  }

  // Apply the currentPad to all lines of formattedContent
  return formattedContent.split('\n').map(line => currentPad + line).join('\n');
}

export const intoPattern = /[Ii][Nn][Tt][Oo][\t\n\r ]+([A-Za-z_][A-Za-z_0-9]*|`[A-Za-z_0-9~!@#$%^&*()-=+[\]{}\\|;:'",.<>?/]*`)[\t\n\r ]*$/;

// Generates a random UUID v4 for use as a local UI identifier.
export function generateUUID(): string {
  return crypto.randomUUID();
}

// Seconds between two ISO timestamps, or null when either is missing/invalid.
export const secondsBetween = (
  a?: string | null, b?: string | null
): number | null => {
  if (!a || !b) return null;
  const ta = new Date(a).getTime();
  const tb = new Date(b).getTime();
  if (!isFinite(ta) || !isFinite(tb)) return null;
  return Math.max(0, (tb - ta) / 1000);
};

// Compact seconds label for job detail rows ("1.5s", "—" when unknown).
export const formatSeconds = (s: number | null): string =>
  s == null ? '—' : `${s.toFixed(1)}s`;
